import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, cn } from '@chapche/ui';
import { propertiesStorage } from '../../../services/propertiesStorage';
import { propertyNamesService } from '../../../services/propertyNamesService';
import {
    PAGE_TAB_NAME_LIST,
    PRODUCT_TAB,
    PROPERTY_TAB_NAME,
} from '../../../constant';
import {
    buildMacronutrientCompositionTree,
    type CompositionProperty,
    type CompositionTreeNode,
} from '../../../services/propertyCompositionTree';

const badgeClass = (kind: string): string => {
    switch (kind) {
        case 'essential':
        case 'efa':
            return 'badge badge_success';
        case 'conditionally-essential':
            return 'badge badge_warning';
        default:
            return 'badge';
    }
};

const nodeLabel = (
    node: CompositionTreeNode,
    i18n: { resolvedLanguage?: string; t: (key: string) => string },
): string => {
    if (node.property) {
        return propertyNamesService.getLocalization(
            i18n.resolvedLanguage,
            node.property,
            true,
            i18n,
        );
    }
    if (node.labelKey) {
        return i18n.t(node.labelKey);
    }
    return node.code || node.id;
};

const HubNode = ({
    node,
    size = 'mid',
}: {
    node: CompositionTreeNode;
    size?: 'root' | 'mid' | 'leaf';
}) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const label = nodeLabel(node, i18n);
    const clickable = Boolean(node.property?.id);

    const openProperty = () => {
        if (node.property?.id) {
            navigate(`/product/property/${node.property.id}/`);
        }
    };

    const sizeClass = size === 'root'
        ? 'min-h-56 min-w-56 px-16 py-12 text-base'
        : size === 'mid'
            ? 'min-h-40 px-12 py-8 text-sm'
            : 'min-h-32 px-10 py-6 text-xs';

    const content = (
        <>
            <span className="font-montserrat-medium text-ink">{label}</span>
            {node.code ? (
                <span className="block__bullet mt-2 block text-[11px] leading-tight">
                    {node.code}
                </span>
            ) : null}
        </>
    );

    return (
        <div className="flex max-w-[220px] flex-col items-center gap-6 text-center">
            {clickable ? (
                <button
                    type="button"
                    onClick={openProperty}
                    className={cn(
                        'box-border inline-flex flex-col items-center justify-center rounded-full',
                        'border border-dashed border-light-grey bg-white text-ink',
                        'transition-colors hover:border-ink hover:bg-disabled',
                        sizeClass,
                    )}
                >
                    {content}
                </button>
            ) : (
                <div
                    className={cn(
                        'box-border inline-flex flex-col items-center justify-center rounded-full',
                        'border border-dashed border-light-grey bg-disabled text-ink',
                        sizeClass,
                    )}
                >
                    {content}
                </div>
            )}
            {node.badges.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-4">
                    {node.badges.map((badge) => (
                        <span key={badge} className={badgeClass(badge)}>
                            {i18n.t(`app.page.property.composition.badge.${badge}`)}
                        </span>
                    ))}
                </div>
            ) : null}
            {node.emptyHintKey ? (
                <span className="block__bullet alert text-[11px]">
                    {i18n.t(node.emptyHintKey)}
                </span>
            ) : null}
        </div>
    );
};

/** Classic org-chart: hub → stem → horizontal bar → child columns. */
const BranchChildren = ({
    nodes,
}: {
    nodes: CompositionTreeNode[];
}) => {
    if (nodes.length === 0) {
        return null;
    }

    return (
        <div className="flex w-full flex-col items-center">
            <div className="h-20 w-px bg-light-grey" aria-hidden />
            <div
                className="relative flex w-full flex-wrap justify-center gap-x-24 gap-y-20 pt-0"
            >
                {nodes.length > 1 ? (
                    <span
                        aria-hidden
                        className="pointer-events-none absolute left-[12%] right-[12%] top-0 h-px bg-light-grey"
                    />
                ) : null}
                {nodes.map((child) => (
                    <div
                        key={child.id}
                        className="relative flex min-w-[140px] max-w-[280px] flex-col items-center"
                    >
                        <span
                            aria-hidden
                            className="mb-0 h-20 w-px bg-light-grey"
                        />
                        <HubNode node={child} size="mid" />
                        {child.children.length > 0 ? (
                            <LeafCluster nodes={child.children} />
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    );
};

const LeafCluster = ({ nodes }: { nodes: CompositionTreeNode[] }) => (
    <div className="mt-0 flex w-full flex-col items-center">
        <div className="h-16 w-px border-l border-dashed border-light-grey" aria-hidden />
        <div className="relative flex w-full flex-col items-stretch gap-8 border-l border-dashed border-light-grey pl-12">
            {nodes.map((leaf) => (
                <div key={leaf.id} className="relative flex items-center gap-8">
                    <span
                        aria-hidden
                        className="absolute -left-12 top-1/2 h-px w-12 border-t border-dashed border-light-grey"
                    />
                    <HubNode node={leaf} size="leaf" />
                </div>
            ))}
        </div>
    </div>
);

const MacroHub = ({ node }: { node: CompositionTreeNode }) => (
    <article className="flex min-w-[280px] flex-1 flex-col items-center px-12 py-16">
        <HubNode node={node} size="root" />
        <BranchChildren nodes={node.children} />
    </article>
);

const PropertyCompositionTreeTab = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const [revision, setRevision] = useState(0);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const onChange = () => {
            setIsLoading(false);
            setRevision((value) => value + 1);
        };
        propertiesStorage.loadItems(onChange);
        propertiesStorage.registerFiltersChangingObserver(onChange);
        return () => {
            propertiesStorage.unRegisterFiltersChangingObserver(onChange);
        };
    }, []);

    const tree = useMemo(() => {
        const items = propertiesStorage.getItems(true) as CompositionProperty[];
        return buildMacronutrientCompositionTree(items || []);
    }, [revision]);

    return (
        <section className="workspace__content w-full">
            <div className="mb-16 flex flex-wrap items-center gap-12">
                <Button
                    variant="outline"
                    onClick={() => navigate(`/${PRODUCT_TAB}/${PROPERTY_TAB_NAME}/${PAGE_TAB_NAME_LIST}`)}
                >
                    {i18n.t('app.page.property.composition.backToList')}
                </Button>
                <span className="block__text">
                    {i18n.t('app.page.property.composition.title')}
                </span>
            </div>
            <p className="block__bullet mb-16">
                {i18n.t('app.page.property.composition.hint')}
            </p>
            {isLoading ? (
                <p className="block__text">{i18n.t('app.page.property.composition.loading')}</p>
            ) : (
                <div
                    key={`composition-tree-${revision}`}
                    className="flex flex-col gap-32 overflow-x-auto lg:flex-row lg:items-start lg:justify-around"
                >
                    {tree.map((root) => (
                        <MacroHub key={root.id} node={root} />
                    ))}
                </div>
            )}
        </section>
    );
};

export default PropertyCompositionTreeTab;
